import { FormControl, InputAdornment, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PropTypes from 'prop-types';
import React from 'react';

const nullableControlValue = value => (value === null || value === undefined ? '' : value);
const nullableBooleanControlValue = value => (value === null || value === undefined ? '' : String(value));
const nestedSelectMenuProps = {
  disableEnforceFocus: true,
};

export const VOUCHER_IMPORT_DATA_TYPES = {
  TEXT: 'Text',
  DATE: 'Date',
  BOOLEAN: 'Boolean',
  ENUMERATION: 'Enumeration',
};

export const VOUCHER_IMPORT_ENUMERATIONS = {
  PAYEE_TYPES: {
    label: 'Payee Type',
    storeKey: 'payeeTypes',
  },
  NOT_PAYABLES_TYPE: {
    label: 'Not Payable Type',
    storeKey: 'notPayables',
  },
  VOUCHER_TYPES: {
    label: 'Voucher Type',
    storeKey: 'voucherTypes',
  },
  BATCH_TYPES: {
    label: 'Batch Type',
    storeKey: 'batchTypes',
  },
  CURRENCIES: {
    label: 'Currency Type',
    storeKey: 'currencies',
  },
};

export const getDataTypeLabel = row => (
  row.dataType === VOUCHER_IMPORT_DATA_TYPES.ENUMERATION && row.enumeration
    ? row.enumeration.label
    : row.dataType
);

const getEnumerationOptions = (enumerationTitles, enumeration) => {
  if (!enumeration || !enumerationTitles[enumeration.storeKey]) return [];

  return enumerationTitles[enumeration.storeKey].map(title => ({
    label: title,
    value: title,
  }));
};

const getEmptyEnumerationOptionLabel = (row, enumerationOptions) => (
  row.defaultValue !== null && row.defaultValue !== undefined && enumerationOptions.some(option => option.value === row.defaultValue)
    ? `Use Default: ${row.defaultValue}`
    : '-- Select a value --'
);

const getBooleanOverrideLabel = (row, value) => {
  if (row.position !== null) return '';

  if (value === 'true') return 'Yes';
  if (value === 'false') return 'No';

  return 'Use Default: No';
};

const getEnumerationOverrideLabel = (row, enumerationOptions, value) => {
  if (row.position !== null) return '';
  if (value === '') return getEmptyEnumerationOptionLabel(row, enumerationOptions);

  const selectedOption = enumerationOptions.find(option => option.value === value);
  return selectedOption ? selectedOption.label : value;
};

const OverrideValueControl = props => {
  const { enumerationTitles, onOverrideValueChange, row } = props;
  const enumerationOptions = getEnumerationOptions(enumerationTitles, row.enumeration);

  if (row.dataType === VOUCHER_IMPORT_DATA_TYPES.BOOLEAN) {
    return (
      <FormControl fullWidth size="small">
        <Select
          displayEmpty
          MenuProps={nestedSelectMenuProps}
          value={nullableBooleanControlValue(row.overrideValue)}
          renderValue={value => getBooleanOverrideLabel(row, value)}
          onChange={event => onOverrideValueChange(row.fieldKey, event.target.value === '' ? null : event.target.value === 'true')}
        >
          <MenuItem value="">Use Default: No</MenuItem>
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (row.dataType === VOUCHER_IMPORT_DATA_TYPES.DATE) {
    return (
      <TextField
        fullWidth
        size="small"
        type="date"
        value={nullableControlValue(row.overrideValue)}
        onChange={event => onOverrideValueChange(row.fieldKey, event.target.value || null)}
      />
    );
  }

  if (row.dataType === VOUCHER_IMPORT_DATA_TYPES.ENUMERATION) {
    return (
      <FormControl fullWidth size="small">
        <Select
          displayEmpty
          MenuProps={nestedSelectMenuProps}
          value={nullableControlValue(row.overrideValue)}
          renderValue={value => getEnumerationOverrideLabel(row, enumerationOptions, value)}
          onChange={event => onOverrideValueChange(row.fieldKey, event.target.value || null)}
        >
          <MenuItem value="">
            {getEmptyEnumerationOptionLabel(row, enumerationOptions)}
          </MenuItem>
          {enumerationOptions.filter(option => option.value !== '').map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      fullWidth
      size="small"
      value={nullableControlValue(row.overrideValue)}
      onChange={event => onOverrideValueChange(row.fieldKey, event.target.value || null)}
    />
  );
};

OverrideValueControl.propTypes = {
  enumerationTitles: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  onOverrideValueChange: PropTypes.func.isRequired,
  row: PropTypes.shape({
    dataType: PropTypes.string.isRequired,
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    enumeration: PropTypes.object,
    fieldKey: PropTypes.string.isRequired,
    overrideValue: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    position: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
};

export const getVoucherImportColumnMappings = props => {
  const { enumerationTitles, onOverrideValueChange, onPositionChange } = props;

  return [
    {
      accessorKey: 'fieldName',
      header: 'Field Name',
      Cell: ({ row }) => (
        <Typography variant="body2">
          {row.original.fieldName}
          {row.original.required && (
            <Typography component="span" color="error"> *</Typography>
          )}
        </Typography>
      ),
    },
    {
      accessorKey: 'position',
      header: 'Position',
      Cell: ({ row }) => (
        <TextField
          error={Boolean(row.original.positionError)}
          fullWidth
          size="small"
          value={nullableControlValue(row.original.position)}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          InputProps={{
            endAdornment: row.original.positionError ? (
              <InputAdornment position="end">
                <Tooltip title={row.original.positionError}>
                  <ErrorOutlineIcon color="error" fontSize="small" />
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
          onChange={event => onPositionChange(row.original.fieldId, event.target.value)}
        />
      ),
    },
    {
      accessorKey: 'overrideValue',
      header: 'Override Value',
      Cell: ({ row }) => (
        <OverrideValueControl
          row={row.original}
          enumerationTitles={enumerationTitles}
          onOverrideValueChange={onOverrideValueChange}
        />
      ),
    },
    {
      accessorKey: 'dataType',
      header: 'Data Type',
      Cell: ({ row }) => (
        <Typography variant="body2">{getDataTypeLabel(row.original)}</Typography>
      ),
    },
    {
      accessorKey: 'dataLevel',
      header: 'Data Level',
    },
  ];
};

getVoucherImportColumnMappings.propTypes = {
  enumerationTitles: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  onOverrideValueChange: PropTypes.func.isRequired,
  onPositionChange: PropTypes.func.isRequired,
};

export const VOUCHER_IMPORT_ROWS = [
  {
    id: 1,
    fieldId: 1,
    fieldKey: 'authorizationId',
    fieldName: 'Authorization ID',
    dataLevel: 'Authorization File',
    columnPosition: null,
    required: true,
    dataType: VOUCHER_IMPORT_DATA_TYPES.TEXT,
    enumeration: null,
    overrideValue: null,
    defaultValue: null,
  },
  {
    id: 6,
    fieldId: 2,
    fieldKey: 'invoiceDate',
    fieldName: 'Invoice Date',
    dataLevel: 'Voucher',
    columnPosition: null,
    required: false,
    dataType: VOUCHER_IMPORT_DATA_TYPES.DATE,
    enumeration: null,
    overrideValue: null,
    defaultValue: null,
  },
  {
    id: 20,
    fieldId: 20,
    fieldKey: 'vendorCode',
    fieldName: 'Partner Code',
    dataLevel: 'Partner',
    columnPosition: null,
    required: true,
    dataType: VOUCHER_IMPORT_DATA_TYPES.TEXT,
    enumeration: null,
    overrideValue: null,
    defaultValue: null,
  },
  {
    id: 8,
    fieldId: 3,
    fieldKey: 'paidTo',
    fieldName: 'Payee ID',
    dataLevel: 'Voucher',
    columnPosition: null,
    required: true,
    dataType: VOUCHER_IMPORT_DATA_TYPES.ENUMERATION,
    enumeration: VOUCHER_IMPORT_ENUMERATIONS.PAYEE_TYPES,
    overrideValue: null,
    defaultValue: null,
  },
  {
    id: 10,
    fieldId: 4,
    fieldKey: 'voucherType',
    fieldName: 'Voucher Type ID',
    dataLevel: 'Voucher',
    columnPosition: null,
    required: true,
    dataType: VOUCHER_IMPORT_DATA_TYPES.ENUMERATION,
    enumeration: VOUCHER_IMPORT_ENUMERATIONS.VOUCHER_TYPES,
    overrideValue: null,
    defaultValue: null,
  },
  {
    id: 12,
    fieldId: 6,
    fieldKey: 'batchType',
    fieldName: 'Batch Type ID',
    dataLevel: 'Voucher',
    columnPosition: null,
    required: true,
    dataType: VOUCHER_IMPORT_DATA_TYPES.ENUMERATION,
    enumeration: VOUCHER_IMPORT_ENUMERATIONS.BATCH_TYPES,
    overrideValue: null,
    defaultValue: null,
  },
  {
    id: 5,
    fieldId: 5,
    fieldKey: 'notPayableId',
    fieldName: 'Not Payable Id',
    dataLevel: 'Voucher',
    columnPosition: null,
    required: true,
    dataType: VOUCHER_IMPORT_DATA_TYPES.ENUMERATION,
    enumeration: VOUCHER_IMPORT_ENUMERATIONS.NOT_PAYABLES_TYPE,
    overrideValue: null,
    defaultValue: null,
  },
  {
    id: 13,
    fieldId: 7,
    fieldKey: 'invoiceNumber',
    fieldName: 'Invoice Number',
    dataLevel: 'Voucher',
    columnPosition: null,
    required: true,
    dataType: VOUCHER_IMPORT_DATA_TYPES.TEXT,
    enumeration: null,
    overrideValue: null,
    defaultValue: null,
  },
  {
    id: 15,
    fieldId: 8,
    fieldKey: 'wireTransfer',
    fieldName: 'Wire Transfer',
    dataLevel: 'Voucher',
    columnPosition: null,
    required: true,
    dataType: VOUCHER_IMPORT_DATA_TYPES.BOOLEAN,
    enumeration: null,
    overrideValue: null,
    defaultValue: false,
  },
  {
    id: 16,
    fieldId: 9,
    fieldKey: 'voucherCurrency',
    fieldName: 'Receipt Currency',
    dataLevel: 'Voucher',
    columnPosition: null,
    required: false,
    dataType: VOUCHER_IMPORT_DATA_TYPES.ENUMERATION,
    enumeration: VOUCHER_IMPORT_ENUMERATIONS.CURRENCIES,
    overrideValue: null,
    defaultValue: 'USD',
  },
  {
    id: 17,
    fieldId: 10,
    fieldKey: 'payInCurrency',
    fieldName: 'Pay In Currency',
    dataLevel: 'Voucher',
    columnPosition: null,
    required: false,
    dataType: VOUCHER_IMPORT_DATA_TYPES.ENUMERATION,
    enumeration: VOUCHER_IMPORT_ENUMERATIONS.CURRENCIES,
    overrideValue: null,
    defaultValue: 'USD',
  },
  {
    id: 19,
    fieldId: 11,
    fieldKey: 'rushPayment',
    fieldName: 'Rush Payment',
    dataLevel: 'Voucher',
    columnPosition: null,
    required: true,
    dataType: VOUCHER_IMPORT_DATA_TYPES.BOOLEAN,
    enumeration: null,
    overrideValue: null,
    defaultValue: false,
  },
  {
    id: 2,
    fieldId: 12,
    fieldKey: 'expenseCode',
    fieldName: 'Expense Code',
    dataLevel: 'Voucher Line Item',
    columnPosition: null,
    required: true,
    dataType: VOUCHER_IMPORT_DATA_TYPES.TEXT,
    enumeration: null,
    overrideValue: null,
    defaultValue: null,
  },
  {
    id: 3,
    fieldId: 13,
    fieldKey: 'payrollDate',
    fieldName: 'Payroll Date',
    dataLevel: 'Voucher Line Item',
    columnPosition: null,
    required: false,
    dataType: VOUCHER_IMPORT_DATA_TYPES.DATE,
    enumeration: null,
    overrideValue: null,
    defaultValue: null,
  },
  {
    id: 4,
    fieldId: 14,
    fieldKey: 'entryDate',
    fieldName: 'Entry Date',
    dataLevel: 'Voucher Line Item',
    columnPosition: null,
    required: true,
    dataType: VOUCHER_IMPORT_DATA_TYPES.DATE,
    enumeration: null,
    overrideValue: null,
    defaultValue: null,
  },
  {
    id: 7,
    fieldId: 15,
    fieldKey: 'description',
    fieldName: 'Description',
    dataLevel: 'Voucher Line Item',
    columnPosition: null,
    required: false,
    dataType: VOUCHER_IMPORT_DATA_TYPES.TEXT,
    enumeration: null,
    overrideValue: null,
    defaultValue: null,
  },
  {
    id: 9,
    fieldId: 16,
    fieldKey: 'payAmount',
    fieldName: 'Pay Amount',
    dataLevel: 'Voucher Line Item',
    columnPosition: null,
    required: false,
    dataType: VOUCHER_IMPORT_DATA_TYPES.TEXT,
    enumeration: null,
    overrideValue: null,
    defaultValue: null,
  },
  {
    id: 18,
    fieldId: 19,
    fieldKey: 'exchangeRateDate',
    fieldName: 'Exchange Date Rate',
    dataLevel: 'Approved Line Item',
    columnPosition: null,
    required: false,
    dataType: VOUCHER_IMPORT_DATA_TYPES.DATE,
    enumeration: null,
    overrideValue: null,
    defaultValue: null,
  },
];